/**
 * Provisioning Service Layer
 * Handles all Firestore operations for the provisioning_items collection
 * Includes BUSINESS LOGIC for auto-generating equipment loadouts
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  ProvisioningItem,
  ProvisioningItemStatus,
  DealCategory,
  DEAL_CATEGORY_LABELS,
  JobType,
  StarlinkKitOption,
  migrateProvisioningStatus,
  classifyProvisioningItem,
  resolveServiceWorkflowKey,
  getServiceWorkflowDef,
  serviceStageKindToStatus,
  initialServiceStageId,
  isLabourLineItem,
} from '../types/theo-workflow.types';
import { getDeal } from './deal.service';
import {
  convertPresitePartsToProvisioning,
  getPresitePartsByDeal,
} from './presite-parts.service';
import { getTenantIdSync, tenantIdReadUnion } from '../hooks/useTenantId';
import { scheduleRecalc } from './project-recalc-trigger';
import { parseStarlinkSpeedTier, type StarlinkSpeedTier } from '@shared/domain/starlink-kit';

const COLLECTION_NAME = 'provisioning_items';

/**
 * Which speed plan is this Starlink kit destined for?
 *
 * The deal's Starlink intake `servicePlan` is the primary source. When it's
 * missing or carries no readable cap (BP-sourced deals, or intake that was
 * superseded by the quote), fall back to the recurring Starlink line on the
 * quote — the quote is the authoritative record of what the customer is
 * actually getting. Returns null when neither says; the provisioner then
 * picks the tier on the card.
 */
export function resolveStarlinkSpeedTier(
  starlinkIntakeFields: Record<string, unknown> | undefined,
  quoteItems: import('@/types').PlaygroundQuoteItem[],
): StarlinkSpeedTier | null {
  const intakePlan = typeof starlinkIntakeFields?.servicePlan === 'string'
    ? starlinkIntakeFields.servicePlan
    : undefined;
  const fromIntake = parseStarlinkSpeedTier(intakePlan);
  if (fromIntake) return fromIntake;

  for (const qi of quoteItems) {
    if (qi.billingType !== 'recurring') continue;
    if (!(qi.name || '').toLowerCase().includes('starlink')) continue;
    const fromQuote = parseStarlinkSpeedTier(qi.name);
    if (fromQuote) return fromQuote;
  }
  return null;
}

/**
 * Strip fields with `undefined` values before writing to Firestore (which
 * rejects them). Deeply walks arrays and plain objects so nested optional
 * fields on materials / metadata don't blow up the whole write.
 *
 * Pre-existing bug surfaced during the Phase 0.5 rules deploy: console
 * was filling with `Function updateDoc() called with invalid data.
 * Unsupported field value: undefined` errors from
 * `refreshJobMaterialsFromProvisioning` because some plannedMaterials
 * carried optional fields (serialNumber, notes, etc.) as `undefined`.
 * The error was caught + logged per-job, so nothing was actually
 * broken — just noisy. This helper makes the writes clean.
 */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return (value as unknown as unknown[]).map((v) => stripUndefined(v)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

// ============================================================================
// EQUIPMENT LOADOUT TEMPLATES
// ============================================================================

/**
 * Equipment loadout templates for each deal category
 * Customize these based on your actual equipment inventory
 */
const LOADOUT_TEMPLATES: Record<DealCategory, Array<{
  itemName: string;
  quantity: number;
  metadata?: Record<string, unknown>;
}>> = {
  [DealCategory.STARLINK]: [
    { itemName: 'Starlink Dish (Gen 2)', quantity: 1 },
    { itemName: 'Starlink Router', quantity: 1 },
    { itemName: 'Ethernet Adapter', quantity: 1 },
    { itemName: 'Mounting Pole', quantity: 1 },
    { itemName: 'Cable (75ft)', quantity: 1 },
    { itemName: 'Power Supply', quantity: 1 },
  ],
  [DealCategory.NBN]: [
    { itemName: 'Modem', quantity: 1 },
    { itemName: 'Patch Lead', quantity: 1 },
    { itemName: 'Cable (Cat6)', quantity: 1, metadata: { unit: 'm', defaultLength: 30 } },
    { itemName: 'Wall Plate', quantity: 1 },
    { itemName: 'Conduit', quantity: 1, metadata: { optional: true } },
  ],
  [DealCategory.SMART_HOME]: [
    { itemName: 'Smart Hub (Google/Alexa)', quantity: 1 },
    { itemName: 'Smart Switches', quantity: 5 },
    { itemName: 'Smart Bulbs', quantity: 8 },
    { itemName: 'Door/Window Sensors', quantity: 6 },
    { itemName: 'Smart Thermostat', quantity: 1 },
    { itemName: 'Indoor Camera', quantity: 2 },
    { itemName: 'Outdoor Camera', quantity: 2 },
    { itemName: 'Wire Connectors', quantity: 1, metadata: { type: 'bulk' } },
  ],
  [DealCategory.WIFI_EXTENSION]: [
    { itemName: 'Access Point', quantity: 1 },
    { itemName: 'POE Injector', quantity: 1 },
    { itemName: 'Cable (Cat6)', quantity: 1, metadata: { unit: 'm', defaultLength: 30 } },
    { itemName: 'Wall Mount', quantity: 1 },
    { itemName: 'Cable Ties', quantity: 1, metadata: { type: 'bulk' } },
  ],
  [DealCategory.SECURITY]: [
    { itemName: 'Security Panel', quantity: 1 },
    { itemName: 'Motion Sensor', quantity: 2 },
    { itemName: 'Door Sensor', quantity: 3 },
    { itemName: 'Siren', quantity: 1 },
    { itemName: 'Keypad', quantity: 1 },
    { itemName: 'Cable', quantity: 1, metadata: { type: 'bulk' } },
  ],
  [DealCategory.VOIP]: [
    { itemName: 'VoIP Handset', quantity: 1 },
    { itemName: 'POE Adapter', quantity: 1 },
    { itemName: 'Patch Lead', quantity: 1 },
    { itemName: 'Power Supply', quantity: 1 },
  ],
  [DealCategory.IT]: [
    { itemName: 'Desktop Computer', quantity: 1, metadata: { optional: true } },
    { itemName: 'Monitor', quantity: 1, metadata: { optional: true } },
    { itemName: 'Keyboard & Mouse', quantity: 1, metadata: { optional: true } },
    { itemName: 'Network Switch', quantity: 1, metadata: { optional: true } },
    { itemName: 'Server/NAS', quantity: 1, metadata: { optional: true } },
    { itemName: 'UPS/Battery Backup', quantity: 1, metadata: { optional: true } },
    { itemName: 'Printer', quantity: 1, metadata: { optional: true } },
    { itemName: 'Ethernet Cables (Cat6)', quantity: 5 },
  ],
  [DealCategory.ACCESS_CONTROL]: [
    { itemName: 'Door Controller', quantity: 1 },
    { itemName: 'Reader (Keypad/Fob)', quantity: 1 },
    { itemName: 'Electric Strike / Maglock', quantity: 1 },
    { itemName: 'Request-to-Exit Button', quantity: 1 },
    { itemName: 'Power Supply', quantity: 1 },
    { itemName: 'Cable', quantity: 1, metadata: { type: 'bulk' } },
  ],
  [DealCategory.AV]: [
    { itemName: 'Display Mount / Bracket', quantity: 1 },
    { itemName: 'AV Receiver / Amplifier', quantity: 1 },
    { itemName: 'Speakers', quantity: 2 },
    { itemName: 'Subwoofer', quantity: 1, metadata: { optional: true } },
    { itemName: 'HDMI Cable', quantity: 2 },
    { itemName: 'Speaker Cable', quantity: 1, metadata: { type: 'bulk' } },
    { itemName: 'Wall Plate / Brush Plate', quantity: 1 },
  ],
  // Fee-for-service deals carry no default hardware loadout — line items are
  // built directly in the quote.
  [DealCategory.FEE_FOR_SERVICE]: [],
};

/**
 * Reduced loadout for BYO Starlink deals (customer supplies their own kit)
 * Only includes installation/mounting materials, not the Starlink hardware itself
 */
const STARLINK_BYO_LOADOUT: Array<{
  itemName: string;
  quantity: number;
  metadata?: Record<string, unknown>;
}> = [
  { itemName: 'Mounting Pole', quantity: 1 },
  { itemName: 'Cable (75ft)', quantity: 1 },
  { itemName: 'Cable Clips & Ties', quantity: 1, metadata: { type: 'bulk' } },
];

// ============================================================================
// CREATE
// ============================================================================

/**
 * Create a single provisioning item
 */
export async function createProvisioningItem(
  data: Omit<ProvisioningItem, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const itemData: Omit<ProvisioningItem, 'id'> = {
    ...data,
    tenantId: (data as { tenantId?: string }).tenantId ?? getTenantIdSync(),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  } as Omit<ProvisioningItem, 'id'>;

  const docRef = await addDoc(collection(db, COLLECTION_NAME), itemData);
  return docRef.id;
}

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single provisioning item by ID
 */
export async function getProvisioningItem(
  itemId: string
): Promise<ProvisioningItem | null> {
  const docRef = doc(db, COLLECTION_NAME, itemId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as ProvisioningItem;
}

/**
 * Get all provisioning items for a specific deal
 */
export async function getProvisioningItemsByDeal(
  dealId: string
): Promise<ProvisioningItem[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('dealId', '==', dealId),
    orderBy('createdAt', 'asc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as ProvisioningItem
  );
}

/**
 * Get provisioning items for a project (Phase 6). Optional waveNumber
 * + dwellingId filters for the WaveBoard UI to narrow on tab/dwelling
 * selection.
 *
 * Composite index needed when both projectId + waveNumber are queried:
 * `(tenantId, projectId, waveNumber, createdAt asc)`. Single-filter
 * queries piggyback off the existing tenantId+createdAt index. The
 * Phase 6 index addition lives in firestore.indexes.json.
 */
export async function getProvisioningItemsByProject(
  projectId: string,
  opts: { waveNumber?: number; dwellingId?: string } = {}
): Promise<ProvisioningItem[]> {
  const baseConstraints = [
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('projectId', '==', projectId),
  ];
  if (opts.waveNumber !== undefined) {
    baseConstraints.push(where('waveNumber', '==', opts.waveNumber));
  }
  if (opts.dwellingId !== undefined) {
    baseConstraints.push(where('projectDwellingId', '==', opts.dwellingId));
  }
  const q = query(
    collection(db, COLLECTION_NAME),
    ...baseConstraints,
    orderBy('createdAt', 'asc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as ProvisioningItem
  );
}

/**
 * Get provisioning items by status
 */
export async function getProvisioningItemsByStatus(
  status: ProvisioningItemStatus
): Promise<ProvisioningItem[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('status', '==', status),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as ProvisioningItem
  );
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * Update a provisioning item
 */
export async function updateProvisioningItem(
  itemId: string,
  data: Partial<Omit<ProvisioningItem, 'id' | 'createdAt' | 'dealId'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, itemId);

  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });

  // P9a.3 — if this item belongs to a project, schedule a cost-rollup
  // recalc (provisioning actuals feed the project's actualCost). The
  // update partial usually doesn't carry projectId, so read it back —
  // best-effort, fire-and-forget, never blocks the update.
  void (async () => {
    try {
      let projectId = (data as { projectId?: string }).projectId;
      if (!projectId) {
        const snap = await getDoc(docRef);
        projectId = snap.exists()
          ? (snap.data().projectId as string | undefined)
          : undefined;
      }
      if (projectId) scheduleRecalc(projectId);
    } catch {
      /* best-effort — recalc trigger must never break the write */
    }
  })();
}

/**
 * Update the status of a provisioning item with tracking timestamps.
 * Automatically sets statusChangedAt, and orderedAt when moving to SHIPPED.
 */
export async function updateProvisioningItemStatus(
  itemId: string,
  status: ProvisioningItemStatus
): Promise<void> {
  const now = new Date().toISOString();
  const updates: Partial<ProvisioningItem> = {
    status,
    statusChangedAt: now,
  };

  // Auto-set orderedAt when marking as shipped
  if (status === ProvisioningItemStatus.SHIPPED) {
    updates.orderedAt = now;
  }

  await updateProvisioningItem(itemId, updates);
}

// ============================================================================
// DELETE
// ============================================================================

/**
 * Delete a provisioning item
 */
export async function deleteProvisioningItem(itemId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, itemId);
  await deleteDoc(docRef);
}

/**
 * Delete all provisioning items for a deal
 */
export async function deleteProvisioningItemsByDeal(dealId: string): Promise<void> {
  const items = await getProvisioningItemsByDeal(dealId);

  const batch = writeBatch(db);
  items.forEach((item) => {
    if (item.id) {
      const docRef = doc(db, COLLECTION_NAME, item.id);
      batch.delete(docRef);
    }
  });

  await batch.commit();
}

// ============================================================================
// 🎯 BUSINESS LOGIC: PROVISIONING LOADOUT GENERATOR
// ============================================================================

/**
 * PROVISIONING GENERATOR - Auto-create equipment checklist
 *
 * This is the core business logic that runs when a deal is WON.
 *
 * NEW BEHAVIOR FOR PRESITE JOBS:
 * - If the deal has a jobType=PRESITE, it will convert presite parts to provisioning
 * - Otherwise, it auto-generates based on category like before
 *
 * Auto-generated categories:
 * - STARLINK → Starlink Dish, Router, Ethernet Adapter, etc.
 * - NBN → Modem, Patch Lead, Cat6 Cable, Wall Plate, etc.
 * - SMART_HOME → Smart Hub, Switches, Bulbs, Cameras, etc.
 * - WIFI_EXTENSION → Access Point, POE Injector, Cat6 Cable, etc.
 * - SECURITY → Security Panel, Motion Sensors, Door Sensors, etc.
 * - IT → Desktop, Monitor, Network Switch, Ethernet Cables, etc.
 * - VOIP → VoIP Handset, POE Adapter, Patch Lead, etc.
 *
 * Uses Firestore batched writes for performance.
 *
 * @param dealId - The deal that was won
 * @returns Array of created item IDs
 */
export async function generateProvisioningLoadout(
  dealId: string
): Promise<string[]> {
  // Get the deal to determine category and job type
  const deal = await getDeal(dealId);

  if (!deal) {
    throw new Error(`Deal ${dealId} not found`);
  }

  // Check if loadout already exists
  const existingItems = await getProvisioningItemsByDeal(dealId);
  if (existingItems.length > 0) {
    console.warn(
      `Provisioning loadout already exists for deal ${dealId}. Skipping generation.`
    );
    return existingItems.map((item) => item.id!);
  }

  // NEW: If this is a PRESITE job, use presite parts instead of auto-generation
  if (deal.jobType === JobType.PRESITE) {
    const presiteParts = await getPresitePartsByDeal(dealId);

    if (presiteParts.length > 0) {
      console.log(
        `[PROVISIONING] Converting ${presiteParts.length} presite parts to provisioning for deal ${dealId}`
      );
      return await convertPresitePartsToProvisioning(dealId);
    } else {
      console.warn(
        `[PROVISIONING] Deal ${dealId} is PRESITE type but has no presite parts. Cannot generate loadout.`
      );
      return [];
    }
  }

  // EXISTING BEHAVIOR: Auto-generate based on category for non-presite jobs
  if (!deal.category) {
    throw new Error(
      `Deal ${dealId} has no category set. Cannot generate loadout.`
    );
  }

  // For BYO Starlink deals, use the reduced install-only template
  const isByoStarlink = deal.category === DealCategory.STARLINK && deal.starlinkKit === StarlinkKitOption.BYO;

  // Get the equipment template for this category
  const template = isByoStarlink ? STARLINK_BYO_LOADOUT : LOADOUT_TEMPLATES[deal.category];

  if (!template || template.length === 0) {
    console.warn(`No loadout template defined for category: ${deal.category}`);
    return [];
  }

  // Create all provisioning items using batched writes
  const batch = writeBatch(db);
  const itemRefs: DocumentReference[] = [];

  template.forEach((templateItem) => {
    const itemRef = doc(collection(db, COLLECTION_NAME));
    itemRefs.push(itemRef);

    const itemData: Omit<ProvisioningItem, 'id'> = {
      // tenantId required by Phase 0.5 Session A rules. Without it, the
      // batch commit fails permission-denied. createProvisioningItem (above)
      // sets it via the type — these batch paths bypass that and need
      // it explicitly.
      tenantId: getTenantIdSync(),
      dealId,
      itemName: templateItem.itemName,
      quantity: templateItem.quantity,
      status: ProvisioningItemStatus.NOT_ORDERED,
      metadata: templateItem.metadata || {},
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    batch.set(itemRef, itemData);
  });

  // Commit the batch
  await batch.commit();

  const itemIds = itemRefs.map((ref) => ref.id);

  console.log(
    `[PROVISIONING] Generated ${template.length} items for deal ${dealId} (${deal.category}${isByoStarlink ? ' - BYO Kit' : ''})`
  );
  console.log(`Items created:`, template.map((t) => t.itemName).join(', '));

  return itemIds;
}

/**
 * Get the loadout template for a specific category (preview)
 * Useful for showing what equipment will be auto-generated
 */
export function getLoadoutTemplateForCategory(category: DealCategory) {
  return LOADOUT_TEMPLATES[category] || [];
}

/**
 * Bulk update status for multiple items
 * Useful for "mark all as ordered" operations
 */
export async function bulkUpdateProvisioningItemStatus(
  itemIds: string[],
  status: ProvisioningItemStatus
): Promise<void> {
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  itemIds.forEach((itemId) => {
    const docRef = doc(db, COLLECTION_NAME, itemId);
    const updates: Record<string, unknown> = {
      status,
      statusChangedAt: now,
      updatedAt: Timestamp.now(),
    };
    if (status === ProvisioningItemStatus.SHIPPED) {
      updates.orderedAt = now;
    }
    batch.update(docRef, updates);
  });

  await batch.commit();
  console.log(`[PROVISIONING] Updated ${itemIds.length} items to status: ${status}`);
}

// ============================================================================
// 🎯 DEAL WORKSPACE: GENERATE PROVISIONING FROM QUOTE
// ============================================================================

/**
 * Generate provisioning items from a finalized quote.
 *
 * Called when a deal moves to WON/PROVISIONING stage.
 * Creates provisioning_items from the quote's PlaygroundQuoteItems:
 * - ALL items (one-time AND recurring) → provisioning items with status NOT_ORDERED
 * - Special workflows detected: porting (VoIP), activation, kit_order (Starlink)
 * - orderByDate sourced from deal.schedulingGuidance.scheduleByDate when available
 *
 * @param dealId - The deal that was won
 * @param quoteItems - The quote items from the active QuoteVersion
 * @param deal - The full deal object (for service module data)
 * @returns Array of created provisioning item IDs
 */
export async function generateProvisioningFromQuote(
  dealId: string,
  quoteItems: import('@/types').PlaygroundQuoteItem[],
  deal: import('@/types/theo-workflow.types').Deal,
  opts: {
    /** Phase 6 — project the items belong to. Set by the auto-action
     *  handlers and by project-driven generation paths. Empty for
     *  pure deal-flows (existing pre-Phase-6 behaviour). */
    projectId?: string;
    /** Phase 6 — dwelling each item is destined for. Empty for
     *  project-wide procurement. */
    dwellingId?: string;
    /** Phase 6 — wave bucket. Set by the cloud-task handlers
     *  (generateWave1Provisioning -> 1, generateWave2 -> 2, etc.). */
    waveNumber?: number;
    /** Phase 6 — optional predicate. When set, only quote items where
     *  `filter(item) === true` are converted. Defaults to "every item"
     *  to preserve existing behaviour. */
    filter?: (item: import('@/types').PlaygroundQuoteItem) => boolean;
  } = {}
): Promise<string[]> {
  // Check if items already exist FOR THIS WAVE. Pre-Phase-6 callers
  // (no wave context) keep the historic "skip if any items exist"
  // behaviour. Wave-aware callers want to be able to add Wave 2 items
  // even when Wave 1 items already landed for the same deal.
  const existingItems = await getProvisioningItemsByDeal(dealId);
  if (opts.waveNumber === undefined) {
    if (existingItems.length > 0) {
      console.warn(
        `[PROVISIONING] Items already exist for deal ${dealId}. Skipping generation.`
      );
      return existingItems.map((item) => item.id!);
    }
  } else {
    // Scope the "already provisioned" check to BOTH wave AND dwelling. Waves
    // are per-dwelling (§3.3/§3.4), so Wave 1 for Dwelling A must NOT suppress
    // Wave 1 generation for Dwelling B on the same deal. Without the dwelling
    // key, the second dwelling's wave is silently skipped entirely.
    const existingForWave = existingItems.filter(
      (it) =>
        (it.waveNumber ?? null) === opts.waveNumber &&
        (it.projectDwellingId ?? null) === (opts.dwellingId ?? null)
    );
    if (existingForWave.length > 0) {
      console.warn(
        `[PROVISIONING] Wave ${opts.waveNumber}${opts.dwellingId ? ` / dwelling ${opts.dwellingId}` : ''} already provisioned for deal ${dealId}. Skipping.`
      );
      return existingForWave.map((item) => item.id!);
    }
  }

  // Apply the optional item filter — defaults to identity so existing
  // callers see no behaviour change.
  if (opts.filter) {
    quoteItems = quoteItems.filter(opts.filter);
  }

  // Strip LABOUR lines before anything is provisioned. Labour ("Labour —
  // {scope}", "Solution installation" style lines, the field-scope
  // "Labour — 2-techs × 4hr" form, anything categorised 'Labour') bills off
  // the deal — never off provisioning_items — so it carries no orderable gear
  // and must not land on /home/provisioning. This is THE funnel: every
  // deal/quote/proposal-derived provisioning path runs through here, so one
  // filter covers them all. Billing / deal value / MRR are unaffected (they
  // read labour from the deal scope, not from provisioning_items).
  const labourCount = quoteItems.filter((qi) => isLabourLineItem(qi)).length;
  if (labourCount > 0) {
    quoteItems = quoteItems.filter((qi) => !isLabourLineItem(qi));
    console.log(
      `[PROVISIONING] Skipped ${labourCount} labour line(s) for deal ${dealId} — labour bills off the deal, never provisioned`,
    );
  }

  const batch = writeBatch(db);
  const itemRefs: DocumentReference[] = [];

  // Resolve orderByDate from scheduling guidance (if captured during deal advancement)
  const schedulingGuidance = (deal as { schedulingGuidance?: Record<string, unknown> }).schedulingGuidance;
  const orderByDate = (schedulingGuidance?.scheduleByDate as string) || undefined;

  // Build supplier lookup from parts_catalog for matching by name/sku
  const supplierMap = new Map<string, string>();
  try {
    const catalogSnap = await getDocs(query(
      collection(db, 'parts_catalog'),
      where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    ));
    catalogSnap.docs.forEach((d) => {
      const data = d.data();
      if (data.supplier) {
        if (data.name) supplierMap.set(data.name.toLowerCase(), data.supplier);
        if (data.sku) supplierMap.set(data.sku.toLowerCase(), data.supplier);
      }
    });
  } catch (err) {
    console.warn('[PROVISIONING] Could not load parts catalog for supplier lookup:', err);
  }

  for (const qi of quoteItems) {
    // RECURRING service / ISP plan lines do NOT become their own provisioning
    // rows — they're billing (Utilmate) and are represented on the Services &
    // ISP tab by a single consolidated service workflow row per service type
    // (built below). Only skip the recurring plan lines: a ONE-OFF
    // service-side line (e.g. an "NBN Connection Fee") is a real thing the
    // provisioner must handle, so it keeps its own row rather than silently
    // vanishing. (Pre-rework this loop created a row for every quote item,
    // which double-listed each plan alongside its "Activate …" item.)
    const lineCategory = classifyProvisioningItem({
      itemName: qi.name,
      billingType: qi.billingType || 'one-time',
      metadata: qi.serviceModule != null ? { serviceModule: qi.serviceModule } : undefined,
    } as ProvisioningItem);
    if (lineCategory === 'service' && qi.billingType === 'recurring') continue;

    const itemRef = doc(collection(db, COLLECTION_NAME));
    itemRefs.push(itemRef);

    // Try to match supplier from catalog by name or sku
    const supplier = supplierMap.get(qi.name?.toLowerCase() || '') ||
      supplierMap.get(qi.sku?.toLowerCase() || '') ||
      undefined;

    // Build metadata — strip undefined values (Firestore rejects them)
    const metadata: Record<string, unknown> = {};
    if (qi.sku != null) metadata.sku = qi.sku;
    if (qi.unitCost != null) metadata.unitCost = qi.unitCost;
    if (qi.unitPrice != null) metadata.unitPrice = qi.unitPrice;
    if (qi.serviceModule != null) metadata.serviceModule = qi.serviceModule;
    if (qi.id != null) metadata.quoteItemId = qi.id;

    const partialItem: Omit<ProvisioningItem, 'id'> = {
      // tenantId required by Phase 0.5 Session A rules — see sibling
      // batch.set sites for rationale.
      tenantId: getTenantIdSync(),
      dealId,
      itemName: qi.name,
      quantity: qi.quantity,
      status: ProvisioningItemStatus.NOT_ORDERED,
      metadata,
      billingType: qi.billingType || 'one-time',
      // Quote-item note (e.g. line-item review note from Quote Review) →
      // provisioning card notes row, when present.
      ...(qi.notes ? { notes: qi.notes } : {}),
      ...(supplier ? { supplier } : {}),
      ...(orderByDate ? { orderByDate } : {}),
      // Phase 6 — project / dwelling / wave tagging. Optional, only set
      // when the caller passed opts. Pre-Phase-6 callers (no opts) write
      // identical docs to before.
      ...(opts.projectId ? { projectId: opts.projectId } : {}),
      ...(opts.dwellingId ? { projectDwellingId: opts.dwellingId } : {}),
      ...(opts.waveNumber !== undefined ? { waveNumber: opts.waveNumber } : {}),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    // Classify as hardware or service based on billing type and name patterns
    partialItem.provisioningCategory = classifyProvisioningItem(partialItem as ProvisioningItem);

    batch.set(itemRef, partialItem);
  }

  // Detect special workflows from deal service modules
  const serviceModules = deal.serviceModules ?? {};

  // Build the set of services actually represented in the quote items.
  // This is the AUTHORITATIVE list of what the customer is getting — NOT
  // deal.selectedServices (which may contain stale intake choices that were
  // superseded by a Better Proposals PDF with different services).
  const quotedServiceModules = new Set<string>();
  for (const qi of quoteItems) {
    if (qi.serviceModule) quotedServiceModules.add(qi.serviceModule);
  }
  // Also check quoteScopes for scoped items that may carry a serviceModule
  if (deal.quoteScopes) {
    for (const scope of deal.quoteScopes) {
      for (const item of scope.items || []) {
        if (item.serviceModule) quotedServiceModules.add(item.serviceModule);
      }
    }
  }
  // Phase 6 (P6.2) — project tagging spread fragment. Every special-
  // workflow item (porting, kit_order, activation, etc.) inherits the
  // same project / dwelling / wave context as the parent quote items
  // that triggered this call. The fragment is computed once and spread
  // into each `batch.set` site below.
  const projectTagging: Record<string, unknown> = {
    ...(opts.projectId ? { projectId: opts.projectId } : {}),
    ...(opts.dwellingId ? { projectDwellingId: opts.dwellingId } : {}),
    ...(opts.waveNumber !== undefined ? { waveNumber: opts.waveNumber } : {}),
  };

  // Derive services from recurring items (service plans) which map to service types
  for (const qi of quoteItems) {
    if (qi.billingType === 'recurring') {
      const name = (qi.name || '').toLowerCase();
      if (name.includes('starlink')) quotedServiceModules.add(DealCategory.STARLINK);
      if (name.includes('nbn') || name.includes('broadband') || name.includes('internet')) quotedServiceModules.add(DealCategory.NBN);
      if (name.includes('voip') || name.includes('sip')) quotedServiceModules.add(DealCategory.VOIP);
      if (name.includes('4g') || name.includes('5g') || name.includes('mobile')) quotedServiceModules.add(DealCategory.IT);
    }
  }

  // Capture VoIP porting context (numbers to port) to fold into the
  // consolidated VoIP service item built below — VoIP no longer gets its own
  // separate "VoIP Number Porting" row; the porting flow IS the VoIP service
  // workflow.
  const voipModule = serviceModules[DealCategory.VOIP];
  const voipIntake = voipModule?.intakeFields as unknown as Record<string, unknown> | undefined;
  const voipNumbersToPort =
    voipIntake?.portingRequired === true ? (voipIntake?.numbersToPort ?? []) : undefined;

  // Starlink provisioning — individual items for each accessory
  // Only create kit order if Starlink actually appears in the QUOTE (not just in selectedServices).
  const starlinkModule = serviceModules[DealCategory.STARLINK];
  const starlinkIntakeFields = starlinkModule?.intakeFields as unknown as Record<string, unknown> | undefined;
  const hasStarlinkKitOrder = starlinkIntakeFields?.starlinkKit === StarlinkKitOption.TECHERY;
  const starlinkInQuote = quotedServiceModules.has(DealCategory.STARLINK);
  const isByo = starlinkIntakeFields?.starlinkKit === StarlinkKitOption.BYO;
  const shouldCreateKitOrder = hasStarlinkKitOrder || (starlinkInQuote && !isByo && !starlinkIntakeFields?.starlinkKit);

  if (shouldCreateKitOrder) {
    // Main Starlink Kit/Dish — always created for Techery-supplied kits
    const modelLabel = starlinkIntakeFields?.starlinkModel
      ? ` (${starlinkIntakeFields.starlinkModel})`
      : '';
    // Which kit to ORDER depends on the speed plan and on who manages the
    // service — STARLINK KIT - 200 vs STARLINK S/M KIT - 200. Seed both axes
    // onto the item so the provisioner sees them on the card without opening
    // the deal; both stay editable on /home/provisioning.
    const starlinkSpeedTier = resolveStarlinkSpeedTier(starlinkIntakeFields, quoteItems);
    const starlinkSelfManaged = starlinkIntakeFields?.starlinkSelfManaged === true;
    const kitRef = doc(collection(db, COLLECTION_NAME));
    itemRefs.push(kitRef);
    batch.set(kitRef, {
      tenantId: getTenantIdSync(),
      dealId,
      itemName: `Starlink Kit${modelLabel}`,
      quantity: 1,
      status: ProvisioningItemStatus.NOT_ORDERED,
      specialWorkflow: 'kit_order',
      provisioningCategory: 'hardware',
      metadata: { serviceModule: DealCategory.STARLINK },
      ...(starlinkSpeedTier ? { starlinkSpeedTier } : {}),
      starlinkSelfManaged,
      ...(orderByDate ? { orderByDate } : {}),
      ...projectTagging,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    // Mount adaptor — if mount type specified
    const mountType = starlinkIntakeFields?.mountType as string | undefined;
    if (mountType) {
      const mountRef = doc(collection(db, COLLECTION_NAME));
      itemRefs.push(mountRef);
      batch.set(mountRef, {
        tenantId: getTenantIdSync(),
        dealId,
        itemName: `Starlink ${mountType} Mount`,
        quantity: 1,
        status: ProvisioningItemStatus.NOT_ORDERED,
        metadata: { serviceModule: DealCategory.STARLINK, accessoryType: 'mount' },
        ...(orderByDate ? { orderByDate } : {}),
        ...projectTagging,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }

    // Pole — if pole required
    if (starlinkIntakeFields?.poleRequired === true) {
      const poleRef = doc(collection(db, COLLECTION_NAME));
      itemRefs.push(poleRef);
      batch.set(poleRef, {
        tenantId: getTenantIdSync(),
        dealId,
        itemName: 'Starlink Mounting Pole',
        quantity: 1,
        status: ProvisioningItemStatus.NOT_ORDERED,
        metadata: { serviceModule: DealCategory.STARLINK, accessoryType: 'pole' },
        ...(orderByDate ? { orderByDate } : {}),
        ...projectTagging,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }

    // Ethernet adaptor — if required
    if (starlinkIntakeFields?.ethernetAdaptor === true) {
      const adaptorRef = doc(collection(db, COLLECTION_NAME));
      itemRefs.push(adaptorRef);
      batch.set(adaptorRef, {
        tenantId: getTenantIdSync(),
        dealId,
        itemName: 'Starlink Ethernet Adaptor',
        quantity: 1,
        status: ProvisioningItemStatus.NOT_ORDERED,
        metadata: { serviceModule: DealCategory.STARLINK, accessoryType: 'ethernet_adaptor' },
        ...(orderByDate ? { orderByDate } : {}),
        ...projectTagging,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    }
  }

  // ── Consolidated service / ISP items (Services & ISP tab) ──────────────
  // ONE workflow row per ISP service type actually in the quote. Each carries
  // a per-service-type stage flow (SERVICE_PROVISIONING_WORKFLOWS) the
  // provisioner steps through. The underlying `status` tracks the active
  // stage's kind (initial/active/held → NOT_ORDERED, complete → DELIVERED) so
  // the deal's scheduling-readiness gate still works.
  //
  // NBN / VoIP / DEFAULT service rows BLOCK scheduling until their workflow
  // reaches its complete stage ("Order ready for checkout" / "Port confirmed")
  // — that's what sends the job out of provisioning. The Starlink service row
  // is a non-blocking placeholder (specialWorkflow:'activation' keeps it out
  // of the readiness gate) that auto-clears when the Starlink kit is ordered —
  // see the Starlink auto-clear effect in Provisioning.tsx.
  const serviceModulesInQuote: DealCategory[] = [
    DealCategory.NBN,
    DealCategory.STARLINK,
    DealCategory.VOIP,
    DealCategory.IT,
  ].filter((svc) => quotedServiceModules.has(svc));

  for (const svc of serviceModulesInQuote) {
    const isStarlink = svc === DealCategory.STARLINK;
    const isVoip = svc === DealCategory.VOIP;

    // Skip the Starlink service placeholder when no kit will be ordered
    // (BYO / customer-supplied). With no kit there's nothing to auto-clear it,
    // so it would sit forever (and, as the only item, could leave the deal
    // needing a manual advance). For BYO Starlink there's no ISP-side work.
    if (isStarlink && !shouldCreateKitOrder) continue;

    const baseLabel = DEAL_CATEGORY_LABELS[svc] ?? svc;
    const workflowKey = resolveServiceWorkflowKey({
      itemName: String(baseLabel),
      metadata: { serviceModule: svc },
    });

    // Prefer the recurring plan line's name so the row reads e.g.
    // "NBN — NBN Superfast 250/25" rather than a generic label. Match by
    // serviceModule first; the fallback matches the resolved workflow (precise
    // for NBN/VoIP/Starlink) but is skipped for DEFAULT/IT where a loose label
    // like "IT" would substring-match many unrelated recurring lines.
    const planLine = quoteItems.find((qi) => {
      if (qi.billingType !== 'recurring') return false;
      if (qi.serviceModule === svc) return true;
      if (workflowKey === 'DEFAULT') return false;
      return (
        resolveServiceWorkflowKey({
          itemName: qi.name,
          metadata: qi.serviceModule != null ? { serviceModule: qi.serviceModule } : undefined,
        }) === workflowKey
      );
    });
    const itemName = planLine?.name ? `${baseLabel} — ${planLine.name}` : `${baseLabel} service`;

    const stageId = initialServiceStageId(workflowKey);
    const stageKind =
      getServiceWorkflowDef(workflowKey).stages.find((s) => s.id === stageId)?.kind ?? 'initial';

    const metadata: Record<string, unknown> = { serviceModule: svc };
    if (isVoip && voipNumbersToPort !== undefined) {
      metadata.serviceDetails = {
        serviceType: 'VoIP',
        workflow: 'porting',
        numbersToPort: voipNumbersToPort,
      };
    }

    const svcRef = doc(collection(db, COLLECTION_NAME));
    itemRefs.push(svcRef);
    batch.set(svcRef, {
      tenantId: getTenantIdSync(),
      dealId,
      itemName,
      quantity: 1,
      status: serviceStageKindToStatus(stageKind),
      provisioningCategory: 'service',
      serviceWorkflowType: workflowKey,
      serviceStage: stageId,
      serviceStageChangedAt: new Date().toISOString(),
      // Starlink: non-blocking placeholder, excluded from the readiness gate
      // via 'activation'. VoIP: keep 'porting' for back-compat (does NOT
      // exclude it from the gate — VoIP must be port-confirmed first).
      ...(isStarlink ? { specialWorkflow: 'activation' as const } : {}),
      ...(isVoip ? { specialWorkflow: 'porting' as const } : {}),
      metadata,
      ...(orderByDate ? { orderByDate } : {}),
      ...projectTagging,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  await batch.commit();

  const itemIds = itemRefs.map((ref) => ref.id);
  console.log(
    `[PROVISIONING] Generated ${itemIds.length} items from quote for deal ${dealId}`
  );

  return itemIds;
}

/**
 * Advance a service item to a stage within its per-service-type workflow
 * (Services & ISP panel). Keeps the underlying `status` in sync with the
 * stage's kind via `serviceStageKindToStatus` (complete → DELIVERED, every
 * other kind → NOT_ORDERED) so the deal's scheduling-readiness gate keeps
 * working — moving a service to its complete stage is what flips it to
 * DELIVERED and lets the deal auto-advance to SCHEDULING. Idempotent.
 */
export async function setServiceStage(
  itemId: string,
  stageId: string,
  opts: { scheduledAt?: string | null; notes?: string; by?: string } = {}
): Promise<void> {
  const ref = doc(db, COLLECTION_NAME, itemId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const item = { id: snap.id, ...(snap.data() as Record<string, unknown>) } as ProvisioningItem;
  const workflowKey = resolveServiceWorkflowKey(item);
  const wf = getServiceWorkflowDef(workflowKey);
  const stage = wf.stages.find((s) => s.id === stageId);
  if (!stage) {
    console.warn(
      `[PROVISIONING] setServiceStage: unknown stage "${stageId}" for workflow ${workflowKey}`
    );
    return;
  }
  const nowIso = new Date().toISOString();
  const update: Record<string, unknown> = {
    serviceWorkflowType: workflowKey,
    serviceStage: stage.id,
    serviceStageChangedAt: nowIso,
    status: serviceStageKindToStatus(stage.kind),
    statusChangedAt: nowIso,
    updatedAt: Timestamp.now(),
  };
  if (opts.by) update.serviceStageBy = opts.by;
  if (opts.scheduledAt !== undefined) update.serviceScheduledAt = opts.scheduledAt ?? '';
  if (opts.notes !== undefined) update.notes = opts.notes;
  await updateDoc(ref, stripUndefined(update));
}


/**
 * Check if all provisioning items are ready for scheduling.
 *
 * Critical items (hardware + gating service workflows) must be AT_OFFICE or
 * DELIVERED. Items excluded from the gate (`specialWorkflow === 'activation'`
 * — post-install activations + the non-blocking Starlink placeholder) don't
 * count, mirroring the real-time check in useProvisioning.ts so a
 * never-shipping item can't wedge a deal in PROVISIONING.
 *
 * NOTE: The primary readiness check is in useProvisioning.ts (hook-based, real-time).
 * This function is an async utility for one-off checks outside of React components.
 */
export async function areProvisioningItemsReady(
  dealId: string
): Promise<boolean> {
  const items = await getProvisioningItemsByDeal(dealId);
  const critical = items.filter((i) => i.specialWorkflow !== 'activation');
  if (critical.length === 0) return true;

  return critical.every((i) => {
    const migrated = migrateProvisioningStatus(i.status);
    return (
      migrated === ProvisioningItemStatus.AT_OFFICE ||
      migrated === ProvisioningItemStatus.DELIVERED
    );
  });
}

/**
 * Create provisioning items for a fault.
 * Called when a fault needs parts/equipment ordered before deployment.
 *
 * @param faultId - The fault document ID
 * @param items - Array of items to provision (name, quantity, notes)
 * @returns Array of created provisioning item IDs
 */
export async function createFaultProvisioningItems(
  faultId: string,
  items: Array<{ name: string; quantity?: number; notes?: string; supplier?: string }>
): Promise<string[]> {
  const batch = writeBatch(db);
  const itemRefs: DocumentReference[] = [];

  for (const item of items) {
    const itemRef = doc(collection(db, COLLECTION_NAME));
    itemRefs.push(itemRef);
    batch.set(itemRef, {
      tenantId: getTenantIdSync(),
      dealId: '', // Empty for fault-sourced items
      faultId,
      sourceType: 'fault',
      itemName: item.name,
      quantity: item.quantity || 1,
      status: ProvisioningItemStatus.NOT_ORDERED,
      notes: item.notes || '',
      ...(item.supplier ? { supplier: item.supplier } : {}),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  }

  await batch.commit();
  const itemIds = itemRefs.map((ref) => ref.id);
  console.log(`[PROVISIONING] Created ${itemIds.length} items for fault ${faultId}`);
  return itemIds;
}

/**
 * Get provisioning items for a fault
 */
export async function getProvisioningItemsByFault(
  faultId: string
): Promise<ProvisioningItem[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tenantId', 'in', tenantIdReadUnion(getTenantIdSync())),
    where('faultId', '==', faultId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as ProvisioningItem[];
}

// ============================================================================
// PROVISIONING → BRIEFING REFRESH
// ============================================================================

/**
 * Refresh install job plannedMaterials and regenerate AI briefing.
 *
 * Called when provisioning items become fully ready for a deal.
 * The AI briefing is generated at job creation time (when deal hits SCHEDULING),
 * but provisioning may not be complete yet — especially in the parallel
 * Schedule & Provision flow. This function re-fetches the latest provisioning
 * items and updates the job's plannedMaterials so the tech sees accurate gear info.
 *
 * Fire-and-forget — failures are logged but never block provisioning flow.
 */
export async function refreshJobMaterialsFromProvisioning(
  dealId: string
): Promise<void> {
  try {
    // 1. Get the deal to find linked install job(s)
    const dealDoc = await getDoc(doc(db, 'deals', dealId));
    if (!dealDoc.exists()) return;

    const deal = dealDoc.data();
    const jobIds: string[] = deal.installJobIds
      ?? (deal.installJobId ? [deal.installJobId] : []);

    if (jobIds.length === 0) return;

    // 2. Fetch current provisioning items. Drop any labour rows that a
    //    pre-fix deal may have leaked — labour is not gear the tech picks/loads,
    //    so it must not appear in the job's plannedMaterials kit list.
    const provItems = (await getProvisioningItemsByDeal(dealId)).filter(
      (i) => !isLabourLineItem(i),
    );
    if (provItems.length === 0) return;

    const plannedMaterials = provItems.map((item: ProvisioningItem, idx: number) => {
      const extra = item as ProvisioningItem & {
        partId?: string;
        name?: string;
        category?: string;
        sku?: string;
        supplier?: string;
      };
      return {
        id: item.id || extra.partId || `prov-${idx}`,
        name: extra.name || item.itemName || 'Unknown item',
        quantity: item.quantity ?? 1,
        status: item.status || 'pending',
        category: extra.category || '',
        sku: extra.sku || '',
        supplier: extra.supplier || '',
      };
    });

    // 3. Update each install job's plannedMaterials

    for (const jobId of jobIds) {
      try {
        const jobDoc = await getDoc(doc(db, 'jobs', jobId));
        if (!jobDoc.exists()) continue;

        const job = { id: jobDoc.id, ...jobDoc.data() } as { id: string; jobState?: string };

        // Skip jobs that are already in progress or completed
        const state = job.jobState || 'SCHEDULED';
        const skipStates = ['WORKING', 'WRAPPING_UP', 'COMPLETED', 'NEEDS_RETURN_VISIT'];
        if (skipStates.includes(state)) continue;

        // Update plannedMaterials on the job. stripUndefined walks
        // the materials array deeply — without it, an optional field
        // (serialNumber, notes, etc.) set to `undefined` rejects the
        // whole write with the SDK's "Unsupported field value" error.
        await updateDoc(doc(db, 'jobs', jobId), stripUndefined({
          plannedMaterials,
          provisioningStatus: 'picked',
          updatedAt: new Date().toISOString(),
        }));

        console.log(`[Provisioning] Refreshed materials for job ${jobId} (deal ${dealId})`);
      } catch (jobErr) {
        console.error(`[Provisioning] Failed to refresh materials for job ${jobId}:`, jobErr);
      }
    }
  } catch (err) {
    console.error(`[Provisioning] refreshJobMaterialsFromProvisioning failed for deal ${dealId}:`, err);
  }
}

// ============================================================================
// CLEAR DEAL FROM PROVISIONING
// ============================================================================

/**
 * Manually clear a deal from the provisioning queue.
 *
 * Sets `provisioningCleared: true` on the deal so it disappears from the
 * provisioning page. Use this when a deal is fully provisioned and the office
 * wants to dismiss it from view (or when it should be excluded for any other
 * reason — e.g. handled outside the system).
 *
 * Does NOT change the deal stage — the deal can still progress through
 * SCHEDULING / REVIEW / etc. via its normal workflow. This is purely a UI
 * dismissal flag for the /home/provisioning page.
 *
 * Adds an activity log entry on the deal for audit.
 *
 * @param dealId - The deal to clear
 * @param clearedBy - userId of the actor
 * @param clearedByName - Display name of the actor
 */
export async function clearDealFromProvisioning(
  dealId: string,
  clearedBy: string,
  clearedByName: string,
): Promise<void> {
  const now = new Date().toISOString();
  const dealRef = doc(db, 'deals', dealId);

  const dealSnap = await getDoc(dealRef);
  if (!dealSnap.exists()) {
    throw new Error(`Deal ${dealId} not found`);
  }

  await updateDoc(dealRef, {
    provisioningCleared: true,
    provisioningClearedAt: now,
    provisioningClearedBy: clearedBy,
    provisioningClearedByName: clearedByName,
    updatedAt: Timestamp.now(),
  });

  // Activity log entry — fire-and-forget, never block the clear
  try {
    const { addActivityLogEntry } = await import('./deal-workflow.service');
    await addActivityLogEntry(
      dealId,
      clearedBy,
      clearedByName,
      'Cleared from provisioning queue',
      'Manually dismissed from the /home/provisioning page.',
    );
  } catch (err) {
    console.error('[Provisioning] Failed to log activity for clear:', err);
  }
}

/**
 * Restore a deal back to the provisioning queue (undo clearDealFromProvisioning).
 */
export async function restoreDealToProvisioning(
  dealId: string,
  restoredBy: string,
  restoredByName: string,
): Promise<void> {
  const dealRef = doc(db, 'deals', dealId);

  const dealSnap = await getDoc(dealRef);
  if (!dealSnap.exists()) {
    throw new Error(`Deal ${dealId} not found`);
  }

  await updateDoc(dealRef, {
    provisioningCleared: false,
    provisioningClearedAt: null,
    provisioningClearedBy: null,
    provisioningClearedByName: null,
    updatedAt: Timestamp.now(),
  });

  try {
    const { addActivityLogEntry } = await import('./deal-workflow.service');
    await addActivityLogEntry(
      dealId,
      restoredBy,
      restoredByName,
      'Restored to provisioning queue',
    );
  } catch (err) {
    console.error('[Provisioning] Failed to log activity for restore:', err);
  }
}
